import { ChangeDetectorRef, Component, inject, OnDestroy, ChangeDetectionStrategy } from '@angular/core';
import { Subject, Observable } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

/**
 * BaseComponent provides memory management utilities for Angular components.
 * It implements OnDestroy and provides a pattern for automatically unsubscribing
 * from observables when the component is destroyed.
 *
 * Usage:
 * ```typescript
 * @Component({...})
 * export class MyComponent extends BaseComponent {
 *   constructor() {
 *     super();
 *
 *     // Use subscribeUntilDestroy to automatically unsubscribe when component is destroyed
 *     this.subscribeUntilDestroy(someObservable$).subscribe(data => {
 *       // Handle data
 *     });
 *   }
 * }
 * ```
 */
@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  template: ''
})
export class BaseComponent implements OnDestroy {
  /**
   * Used to manually trigger change detection after async mutations
   * (subscribe/setTimeout callbacks) that zoneless CD can't observe on its own.
   */
  protected cdr = inject(ChangeDetectorRef);

  /**
   * Subject that emits when the component is destroyed.
   * Used with takeUntil operator to automatically unsubscribe from observables.
   */
  protected destroy$ = new Subject<void>();

  /**
   * Lifecycle hook that is called when the component is destroyed.
   * Emits on the destroy$ subject and completes it to prevent memory leaks.
   */
  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Helper method to subscribe to an observable until the component is destroyed.
   * @param observable The observable to subscribe to
   * @returns The same observable with takeUntil operator applied
   */
  protected subscribeUntilDestroy<T>(observable: Observable<T>): Observable<T> {
    return observable.pipe(takeUntil(this.destroy$));
  }
}
